<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  credits: {
    type: Object,
    default: () => ({
      available: 0,
      reserved: 0,
      cycle: null,
      topups: [],
      next_expiration_at: null,
      notices: [],
    }),
  },
});
const { t } = useI18n();

const sourceLabels = {
  plan: t('BILLING_SETTINGS.CREDITS.SOURCE.PLAN'),
  trial: t('BILLING_SETTINGS.CREDITS.SOURCE.TRIAL'),
  topup: t('BILLING_SETTINGS.CREDITS.SOURCE.TOPUP'),
  legacy: t('BILLING_SETTINGS.CREDITS.SOURCE.LEGACY'),
};

const lots = computed(() =>
  [props.credits.cycle, ...(props.credits.topups || [])].filter(Boolean)
);

const formatNumber = value =>
  new Intl.NumberFormat(undefined, { useGrouping: false }).format(value || 0);
const noticeMessage = notice => {
  if (notice.event_type === 'agent_credits_low_balance') {
    return t('BILLING_SETTINGS.CREDITS.LOW_BALANCE_NOTICE', {
      available: formatNumber(notice.event_data?.available),
    });
  }

  if (notice.event_type === 'agent_credits_exhausted') {
    return t('BILLING_SETTINGS.CREDITS.EXHAUSTED_NOTICE');
  }

  return null;
};
const visibleNotices = computed(() =>
  (props.credits.notices || [])
    .map(notice => ({ ...notice, message: noticeMessage(notice) }))
    .filter(notice => notice.message)
);
const balanceLabel = lot =>
  `${formatNumber(lot.remaining)} / ${formatNumber(lot.original)}`;
const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(undefined, { timeZone: 'UTC' }).format(
        new Date(value)
      )
    : '';
</script>

<template>
  <div class="grid gap-5">
    <dl class="grid grid-cols-2 gap-4 sm:grid-cols-3">
      <div>
        <dt class="text-xs text-n-slate-10">
          {{ $t('BILLING_SETTINGS.CREDITS.AVAILABLE') }}
        </dt>
        <dd class="mt-1 text-xl font-medium tabular-nums text-n-slate-12">
          {{ formatNumber(credits.available) }}
        </dd>
      </div>
      <div>
        <dt class="text-xs text-n-slate-10">
          {{ $t('BILLING_SETTINGS.CREDITS.RESERVED') }}
        </dt>
        <dd class="mt-1 text-xl font-medium tabular-nums text-n-slate-12">
          {{ formatNumber(credits.reserved) }}
        </dd>
      </div>
      <div v-if="credits.next_expiration_at" class="col-span-2 sm:col-span-1">
        <dt class="text-xs text-n-slate-10">
          {{ $t('BILLING_SETTINGS.CREDITS.NEXT_EXPIRATION') }}
        </dt>
        <dd class="mt-1 text-sm font-medium text-n-slate-12">
          {{ formatDate(credits.next_expiration_at) }}
        </dd>
      </div>
    </dl>

    <div v-if="lots.length" class="grid gap-3 sm:grid-cols-2">
      <article
        v-for="lot in lots"
        :key="lot.id"
        data-test="credit-lot"
        :data-credit-source="lot.source"
        class="rounded-lg border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ sourceLabels[lot.source] }}
          </span>
          <span class="text-sm tabular-nums text-n-slate-11">
            {{ balanceLabel(lot) }}
          </span>
        </div>
        <p class="mt-2 text-xs text-n-slate-10">
          {{
            $t('BILLING_SETTINGS.CREDITS.EXPIRES_ON', {
              date: formatDate(lot.expires_at),
            })
          }}
        </p>
      </article>
    </div>

    <div v-if="visibleNotices.length" class="grid gap-2">
      <p
        v-for="notice in visibleNotices"
        :key="notice.id || notice.occurred_at"
        class="rounded-lg bg-n-amber-2 p-3 text-sm text-n-amber-11"
      >
        {{ notice.message }}
      </p>
    </div>
  </div>
</template>
